import { IFilter } from "@saleor/components/Filter";
import { AutocompleteFilterOpts, FilterOpts, MinMax } from "@saleor/types";
import {
  createAutocompleteField,
  createDateField,
  createOptionsField
} from "@saleor/utils/filters/fields";
import { defineMessages, IntlShape } from "react-intl";

export enum StoreOrdersBiFilterKeys {
  brand = "brand",
  mall = "mall",
  orderDate = "orderDate",
  isPayoutEligible = "isPayoutEligible",
  completedAt = "completedAt",
  shippingMethodName = "shippingMethodName",
  orderStatus = "orderStatus"
}

export enum orderStatus {
  newOrder = "new order",
  acknowledged = "acknowledged",
  ready = "ready",
  collected = "collected",
  receivedAtFc = "received at fc",
  packed = "packed",
  readyToShip = "ready to ship",
  shipped = "shipped",
  completed = "completed",
  canceled = "canceled"
}
export enum StoreOrderBoolean {
  true = "true",
  false = "false"
}
export enum deliveryType {
  EXPRESS = "express",
  IN_STORE_PICKUP = "in_store_pickup",
  STANDARD_DELIVERY = "next_day",
  SAME_DAY_DELIVERY = "same_day",
  NATIONWIDE_DELIVERY = "nationwide_delivery"
}

export interface StoreOrdersBiListFilterOpts {
  orderDate: FilterOpts<MinMax>;
  brand: FilterOpts<string[]> & AutocompleteFilterOpts;
  mall: FilterOpts<string[]> & AutocompleteFilterOpts;
  isPayoutEligible: FilterOpts<string>;
  shippingMethodName: FilterOpts<string[]>;
  completedAt: FilterOpts<MinMax>;
  orderStatus: FilterOpts<string[]>;
}

const messages = defineMessages({
  acknowledged: {
    defaultMessage: "ACKNOWLEDGED",
    description: "acknowledged"
  },
  brand: {
    defaultMessage: "Brand",
    description: "brand"
  },
  canceled: {
    defaultMessage: "CANCELED",
    description: "canceled status"
  },
  collected: {
    defaultMessage: "COLLECTED",
    description: "collected status"
  },
  completed: {
    defaultMessage: "COMPLETED",
    description: "completed status"
  },
  completedAt: {
    defaultMessage: "Order Completion Date",
    description: "order completion date"
  },
  express: {
    defaultMessage: "Express",
    description: "express"
  },
  false: {
    defaultMessage: "FALSE",
    description: "false"
  },
  inStorePickup: {
    defaultMessage: "In Store Pickup ",
    description: "in store pickup "
  },
  isPayoutEligible: {
    defaultMessage: "Payout Eligible",
    description: "payout eligible"
  },
  mall: {
    defaultMessage: "Mall",
    description: "mall name"
  },
  nationwideDelivery: {
    defaultMessage: "Nationwide Delivery",
    description: "nationwide delivery"
  },
  newOrder: {
    defaultMessage: "NEW ORDER",
    description: "new order"
  },
  orderDate: {
    defaultMessage: "Order Created Date",
    description: "order created date"
  },
  orderStatus: {
    defaultMessage: "Order Status",
    description: "status"
  },
  packed: {
    defaultMessage: "PACKED",
    description: "packed status"
  },
  ready: {
    defaultMessage: "READY",
    description: "ready"
  },
  readyToShip: {
    defaultMessage: "READY TO SHIP",
    description: "ready to ship status"
  },
  receivedAtFc: {
    defaultMessage: "RECEIVED AT FC",
    description: "received at fc status"
  },
  sameDayDelivery: {
    defaultMessage: "Same Day Delivery",
    description: "same day delivery"
  },
  shipped: {
    defaultMessage: "SHIPPED",
    description: "shipped status"
  },
  shippingMethodName: {
    defaultMessage: "Delivery Type",
    description: "delivery type"
  },
  standardDelivery: {
    defaultMessage: "Standard Delivery",
    description: "standard delivery"
  },
  true: {
    defaultMessage: "TRUE",
    description: "true"
  }
});

export function createFilterStructure(
  intl: IntlShape,
  opts: StoreOrdersBiListFilterOpts
): IFilter<StoreOrdersBiFilterKeys> {
  return [
    {
      ...createDateField(
        StoreOrdersBiFilterKeys.orderDate,
        intl.formatMessage(messages.orderDate),
        opts.orderDate.value
      ),
      active: opts.orderDate.active
    },
    {
      ...createDateField(
        StoreOrdersBiFilterKeys.completedAt,
        intl.formatMessage(messages.completedAt),
        opts.completedAt.value
      ),
      active: opts.completedAt.active
    },
    {
      ...createAutocompleteField(
        StoreOrdersBiFilterKeys.brand,
        intl.formatMessage(messages.brand),
        opts.brand.value,
        opts.brand.displayValues,
        true,
        opts.brand.choices,
        {
          hasMore: opts.brand.hasMore,
          initialSearch: "",
          loading: opts.brand.loading,
          onFetchMore: opts.brand.onFetchMore,
          onSearchChange: opts.brand.onSearchChange
        }
      ),
      active: opts.brand.active
    },
    {
      ...createAutocompleteField(
        StoreOrdersBiFilterKeys.mall,
        intl.formatMessage(messages.mall),
        opts.mall.value,
        opts.mall.displayValues,
        true,
        opts.mall.choices,
        {
          hasMore: opts.mall.hasMore,
          initialSearch: "",
          loading: opts.mall.loading,
          onFetchMore: opts.mall.onFetchMore,
          onSearchChange: opts.mall.onSearchChange
        }
      ),
      active: opts.mall.active
    },
    {
      ...createOptionsField(
        StoreOrdersBiFilterKeys.orderStatus,
        intl.formatMessage(messages.orderStatus),
        opts.orderStatus.value,
        true,
        [
          {
            label: intl.formatMessage(messages.newOrder),
            value: orderStatus.newOrder
          },
          {
            label: intl.formatMessage(messages.acknowledged),
            value: orderStatus.acknowledged
          },
          {
            label: intl.formatMessage(messages.ready),
            value: orderStatus.ready
          },
          {
            label: intl.formatMessage(messages.collected),
            value: orderStatus.collected
          },
          {
            label: intl.formatMessage(messages.receivedAtFc),
            value: orderStatus.receivedAtFc
          },
          {
            label: intl.formatMessage(messages.packed),
            value: orderStatus.packed
          },
          {
            label: intl.formatMessage(messages.readyToShip),
            value: orderStatus.readyToShip
          },
          {
            label: intl.formatMessage(messages.shipped),
            value: orderStatus.shipped
          },
          {
            label: intl.formatMessage(messages.completed),
            value: orderStatus.completed
          },
          {
            label: intl.formatMessage(messages.canceled),
            value: orderStatus.canceled
          }
        ]
      ),
      active: opts.isPayoutEligible.active
    },
    {
      ...createOptionsField(
        StoreOrdersBiFilterKeys.shippingMethodName,
        intl.formatMessage(messages.shippingMethodName),
        opts.shippingMethodName.value,
        true,
        [
          {
            label: intl.formatMessage(messages.express),
            value: deliveryType.EXPRESS
          },
          {
            label: intl.formatMessage(messages.standardDelivery),
            value: deliveryType.STANDARD_DELIVERY
          },
          {
            label: intl.formatMessage(messages.inStorePickup),
            value: deliveryType.IN_STORE_PICKUP
          },
          {
            label: intl.formatMessage(messages.sameDayDelivery),
            value: deliveryType.SAME_DAY_DELIVERY
          },
          {
            label: intl.formatMessage(messages.nationwideDelivery),
            value: deliveryType.NATIONWIDE_DELIVERY
          }
        ]
      ),
      active: opts.shippingMethodName.active
    }
  ];
}
